import { z } from 'zod'

// a gross -> commission -> tax -> net realized P&L breakdown for one window (today or all-time).
// gross is the average-cost price-only realized P&L; commission is the actual broker commission
// from the matched operations (the window's cash cost, buys of still-open lots included).
// tax is the FIFO-matched realized gain NET OF LOSSES and NET OF the deductible round-trip
// commission, times the user's dividend-tax rate. the floor at 0 is applied ACROSS the account
// rather than per instrument (a losing instrument's loss shrinks the pool everyone is taxed out of
// instead of being discarded), and the pooled figure is allocated back across the instruments that
// gained. every tax is therefore >= 0, and the `instruments[]` taxes sum to the account total.
// NOTE the `allTime` window spans calendar years, which the real tax base does not — treat it as
// indicative rather than a liability. net = gross - commission - tax.
export const dayTradePnlBreakdownSchema = z.object({
  gross: z.number(),
  commission: z.number(),
  tax: z.number(),
  net: z.number(),
})
export type DayTradePnlBreakdownDto = z.infer<typeof dayTradePnlBreakdownSchema>

// one fill behind the currently displayed average entry, with the running state it produced.
// a BUY blends into the running average; a SELL reduces only the quantity and leaves the average
// alone. the sells are part of the ledger on purpose: after a partial sell the average is no longer
// the plain mean of the buys, so a buys-only view would not reconcile with the displayed avgEntry.
export const dayTradeEntryLedgerRowSchema = z.object({
  side: z.enum(['BUY', 'SELL']),
  price: z.number(),
  lots: z.number(),
  date: z.coerce.date(),
  // average-cost entry and lots held AFTER this fill
  avgEntry: z.number(),
  lotsHeld: z.number(),
})
export type DayTradeEntryLedgerRowDto = z.infer<typeof dayTradeEntryLedgerRowSchema>

// per-instrument realized P&L breakdown, recomputed from that instrument's full order ledger.
export const dayTradeInstrumentStatsSchema = z.object({
  instrumentUid: z.string(),
  ticker: z.string().nullable(),
  today: dayTradePnlBreakdownSchema,
  allTime: dayTradePnlBreakdownSchema,
  // the fills since the position was last flat — what built the current avgEntry. empty when flat.
  entryLedger: z.array(dayTradeEntryLedgerRowSchema).default([]),
})
export type DayTradeInstrumentStatsDto = z.infer<typeof dayTradeInstrumentStatsSchema>

// account-level day-trade realized P&L breakdown: the per-instrument rows plus the summed
// today/all-time aggregates, and the tax rate the figures were computed with.
// firstTradeAt/elapsedDays drive the all-time average-daily ROI: elapsedDays is the inclusive
// count of UTC calendar days from the first executed fill to today (1+ once any trade exists,
// 0 when there are no trades), so avg daily return = allTime.net / capitalBase / elapsedDays.
export const dayTradeBotStatsResponseSchema = z.object({
  taxRatePercent: z.number(),
  instruments: z.array(dayTradeInstrumentStatsSchema),
  today: dayTradePnlBreakdownSchema,
  allTime: dayTradePnlBreakdownSchema,
  // earliest executed day-trade fill across all instruments; null when there are no trades
  firstTradeAt: z.coerce.date().nullable(),
  // inclusive UTC calendar days since firstTradeAt; 0 when there are no trades
  elapsedDays: z.number().int().nonnegative(),
})
export type DayTradeBotStatsResponseDto = z.infer<typeof dayTradeBotStatsResponseSchema>
